<script setup lang="ts">
import { computed, watch } from 'vue';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { syncTutorialLanguage } from '@/shared/utils/tutorialUrls';

/**
 * 전체화면 튜토리얼/가이드 뷰어.
 * walkthrough HTML 을 iframe 으로 띄운다. Help 메뉴와 Welcome 모달이 공용으로 사용.
 */
const props = defineProps<{
  modelValue: boolean;
  url: string;
  title?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const languageStore = useLanguageStore();

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
});

// iframe 이 마운트되기 전(flush: 'pre')에 현재 앱 언어를 lt_lang 으로 동기화
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) syncTutorialLanguage(languageStore.getCurrentLanguage());
  },
  { immediate: true }
);

function close() {
  open.value = false;
}

function openInNewWindow() {
  if (props.url) {
    window.open(props.url, '_blank', 'noopener,width=1100,height=820');
  }
}
</script>

<template>
  <v-dialog
    v-model="open"
    :scrim="true"
    fullscreen
    transition="dialog-bottom-transition"
  >
    <v-card style="height: 100vh">
      <v-toolbar density="comfortable" color="primary">
        <v-toolbar-title class="text-white">{{ title || 'Tutorial' }}</v-toolbar-title>
        <v-spacer />
        <v-btn
          variant="text"
          class="text-white"
          size="small"
          aria-label="Open in new window"
          @click="openInNewWindow"
        >
          <v-icon start size="18">mdi-open-in-new</v-icon>
          Open in new window
        </v-btn>
        <v-btn
          icon
          variant="text"
          class="text-white"
          aria-label="Close tutorial"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text style="padding: 0; height: calc(100vh - 64px); background: #0a1628">
        <iframe
          v-if="open && url"
          :src="url"
          title="Tutorial walkthrough"
          style="width: 100%; height: 100%; border: 0; display: block"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
